/* eslint-disable no-undef */
import { takeLatest, put, call, all } from 'redux-saga/effects';
import {
  signInSuccess,
  signInFailure,
  signOutFailure,
  signOutSuccess,
  fetchDataSuccess,
  fetchDataFailure,
  setSignInLoading
} from '../actions';
import history from '../history';
import { sendRequest } from '../utils/dateHelpers';
import { normalizeCompanyConfigurationsContainer } from '../utils/companyConfigurations';
import {
  ensureAnalyticsSessionId,
  clearAnalyticsSessionId
} from '../utils/analytics';

const isAuthLoginPath = pathname =>
  pathname === '/signIn' || pathname === '/login';

const getCompanyConfigurationsOrNull = async () => {
  try {
    return await sendRequest('/company-configurations', 'GET');
  } catch (error) {
    return null;
  }
};

const mergeDashboardPayloadWithCompanyConfigurations = (
  dashboardPayload,
  companyConfigurationsPayload
) => {
  const normalizedCompanyConfigurations =
    normalizeCompanyConfigurationsContainer(companyConfigurationsPayload);

  return {
    ...(dashboardPayload || {}),
    ...normalizedCompanyConfigurations,
    user: {
      ...(dashboardPayload?.user || {}),
      ...normalizedCompanyConfigurations
    }
  };
};

function* loadMergedDashboardInfo() {
  const [dashboardInfo, companyConfigurationsPayload] = yield all([
    call(sendRequest, '/dashboard/filter-info', 'GET'),
    call(getCompanyConfigurationsOrNull)
  ]);

  return mergeDashboardPayloadWithCompanyConfigurations(
    dashboardInfo,
    companyConfigurationsPayload
  );
}

function* signInSaga(action) {
  const { username, password, verifyToken, navigate } = action.payload;

  try {
    yield put(setSignInLoading(true));

    const response = yield call(sendRequest, '/login', 'POST', {
      username,
      password,
      verifyToken
    });
    if (response.message === 'SUCCESS') {
      // Store custom token in local storage or handle login success
      localStorage.setItem('authToken', response.token);
      ensureAnalyticsSessionId();

      const mergedDashboardInfo = yield call(loadMergedDashboardInfo);

      yield put(fetchDataSuccess(mergedDashboardInfo));
      yield put(signInSuccess(response.user));
      if (isAuthLoginPath(window.location.pathname)) {
        navigate('/');
      }
    } else {
      console.error(
        'Failed to authenticate with backend:',
        response.data.error
      );
      yield put(signInFailure(response.error));
    }
  } catch (error) {
    console.error('Error signing in with Google:', error);
    yield put(signInFailure(error.message));
  } finally {
    yield put(setSignInLoading(false)); // Set loading to false
  }
}

function* signOutSaga() {
  try {
    // const responseDataEndPoint = yield call(sendRequest, '/sign-out', 'GET');
    // if (responseDataEndPoint.success) {
    localStorage.removeItem('authToken');
    clearAnalyticsSessionId();
    yield put(signOutSuccess());
    history.push('/');
    // }
  } catch (error) {
    yield put(signOutFailure(error.message));
  }
}

function* fetchDataSaga() {
  try {
    // taşındı watchAuthStateChange
  } catch (error) {
    console.error('_____errror', error);
    yield put(fetchDataFailure(error.message));
  }
}

function* refreshUserInfoSaga() {
  try {
    const token = localStorage.getItem('authToken');
    if (token === null) {
      return;
    }

    const responseData = yield call(loadMergedDashboardInfo);

    if (responseData?.user) {
      yield put(fetchDataSuccess(responseData));
      yield put(signInSuccess(responseData.user));
    }
  } catch (error) {
    console.error('Error refreshing user info:', error);
  }
}

function* watchAuthStateChange() {
  try {
    const token = localStorage.getItem('authToken');
    if (token === null) {
      throw new Error(`no token`);
    }
    ensureAnalyticsSessionId();

    const responseData = yield call(loadMergedDashboardInfo);

    if (responseData?.user) {
      yield put(fetchDataSuccess(responseData));
      yield put(signInSuccess(responseData.user));
      if (isAuthLoginPath(window.location.pathname)) {
        window.location.href = '/';
      }
    } else {
      clearAnalyticsSessionId();
      yield put(signOutSuccess());
    }
  } catch (error) {
    clearAnalyticsSessionId();
    yield put(fetchDataFailure(error.message));
    yield put(signOutSuccess());
    yield put(signInFailure(error.message));
  }
}

export default function* rootSaga() {
  yield takeLatest('WATCH_AUTH_STATE', watchAuthStateChange);
  yield takeLatest('SIGN_IN', signInSaga);
  yield takeLatest('FETCH_DATA_REQUEST', fetchDataSaga);
  yield takeLatest('REFRESH_USER_INFO_REQUEST', refreshUserInfoSaga);
  yield takeLatest('SIGN_OUT_REQUEST', signOutSaga);
}
